"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const TRANSITIONS = [
  { from: "Draft", to: "Open, Cancelled" },
  { from: "Open", to: "Closed, Draft, Cancelled" },
  { from: "Partially Used", to: "Closed only" },
  {
    from: "Used",
    to: "Nothing. Mark As is hidden. Apply and refund have already used the balance",
  },
  {
    from: "Closed",
    to: "Open, Draft — blocked if anything was applied to bills or refunded",
  },
  {
    from: "Cancelled",
    to: "Draft, Open — except a Goods Return, which cannot be uncancelled",
  },
];

const TYPES = [
  {
    type: "Goods Return",
    result:
      "The only type that takes quantity out of the warehouse (return to vendor). Cancel or uncancel of this type is blocked after stock was released",
  },
  {
    type: "Goods Rejection, Rate Difference, Quantity Shortage, Tax Adjustment, Post Sale Discount, Service Cancellation, Other",
    result: "Value only. Warehouse quantity does not move",
  },
];

const RULES = [
  {
    topic: "Ledger posts on save, including Draft",
    rule: "A debit note posts to the general ledger as soon as it is saved. Open → Partially Used → Used / Closed does not re-post. Cancelled reverses the journal",
  },
  {
    topic: "Subscription gate",
    rule: "Only creating a debit note is limited by your plan. Editing, Mark As, Apply to Bill, refund, and deleting are not separately gated",
  },
  {
    topic: "No primary-bank gate",
    rule: "Unlike Credit Notes on the sales side, creating a debit note does not require a primary bank",
  },
  {
    topic: "Debit note number",
    rule: "Auto-generated unless you turn that off under Debit Note Configuration on the form. Default prefix is VN. That same drawer stores Terms & Conditions. Locked after save",
  },
  {
    topic: "A bill is required",
    rule: "Pick a pending bill for this vendor, or tick Use Bill No and type the number. You cannot save without one of those",
  },
  {
    topic: "Division, Branch, Warehouse, Vendor",
    rule: "All four are required to create, plus type, date, shipping address, and at least one item. After save, location and vendor stay locked",
  },
  {
    topic: "Currency",
    rule: "Taken from the linked bill when you pick one, otherwise from the vendor. Cross-currency apply to a different bill is blocked. An exchange-rate field appears when the currency differs from the company base",
  },
  {
    topic: "Current Balance moves on create",
    rule: "Saving reduces the vendor's Current Balance on this branch by the debit note total, including a Draft. Cancel or delete puts that amount back",
  },
  {
    topic: "Only Goods Return deducts stock",
    rule: "That type reduces warehouse quantity. Other types do not touch stock. This is the debit note type — not Inventory Config Stock Deduct Source",
  },
  {
    topic: "You can only edit a Draft",
    rule: "Open, Partially Used, Used, Closed, and Cancelled cannot be edited",
  },
  {
    topic: "Apply to Bill is not bill-header Apply Credit",
    rule: "Apply to Bill on this module uses this debit note's available balance against Open, Partially Paid, or Overdue bills. Apply Credit on a bill detail is opening-balance credit from the vendor branch — a different action",
  },
  {
    topic: "Apply and refund only while Open or Partially Used",
    rule: "Draft, Used, and Closed cannot be applied. Refund also needs available balance greater than zero. Used cannot change status at all",
  },
  {
    topic: "Cancelled Goods Return cannot come back",
    rule: "Uncancel is blocked because stock was released on cancel. Other types can Mark As Draft or Open from Cancelled. Cancel is blocked while refunds exist — delete the refunds first",
  },
  {
    topic: "Delete",
    rule: "Hidden once any bill application or refund exists. The API also refuses Used, Closed, and Partially Used. Draft, Open, and Cancelled with nothing applied can be deleted",
  },
  {
    topic: "No Duplicate, no list Export, no Rejection Order convert",
    rule: "Rejection Orders convert to a Credit Note or PC-to-PC Return, not a Debit Note",
  },
  {
    topic: "Attachments",
    rule: "Up to 3 files, 5 MB each",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. Creating a debit note" },
      { heading: "2. Type — especially Goods Return" },
      { heading: "3. Viewing, searching, and the list" },
      { heading: "4. Editing a debit note" },
      { heading: "5. Printing and downloading" },
      { heading: "6. Changing status" },
      { heading: "7. Apply to Bill" },
      { heading: "8. Refund" },
      { heading: "9. Deleting a debit note" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

export default function DebitNotesPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Debit Notes
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone reducing what you still owe a vendor — returns,
            rate or tax adjustments — then applying that credit to bills or
            refunding it
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            A <B>Debit Note</B> is a purchase adjustment against a bill.
            Save makes it <B>Draft</B> and already posts the ledger and
            reduces vendor Current Balance. <B>Mark As Open</B> is when you
            can <B>Apply to Bill</B> or <B>Refund</B>. That apply is this
            debit note&apos;s available balance — not opening-balance{" "}
            <B>Apply Credit</B> on a bill header.
          </p>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Creating a debit note
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Pick Division, Branch, Warehouse. There is only <B>Save</B>.
            That creates a <B>Draft</B>. There is no Save &amp; Open.
          </p>
          <Definitions
            items={[
              {
                term: "Debit Note#",
                desc: (
                  <>
                    Auto-generated unless you turn that off under{" "}
                    <B>Debit Note Configuration</B> on the form. Default
                    prefix <B>VN</B>. Terms &amp; Conditions live in that
                    drawer. Locked after save.
                  </>
                ),
              },
              {
                term: "Bill",
                desc: (
                  <>
                    Required. Pick a pending bill, or tick <B>Use Bill No</B>{" "}
                    and type the number.
                  </>
                ),
              },
              {
                term: "Vendor, type, items",
                desc: (
                  <>
                    Vendor required. Type chooses whether stock moves (see
                    next section). At least one item.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Add Debit Note with Bill picker, type, Debit Note Configuration, and Save." />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Type — especially Goods Return
          </h3>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Type
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Result
                  </th>
                </tr>
              </thead>
              <tbody>
                {TYPES.map((t, i) => (
                  <tr
                    key={t.type}
                    className={
                      i < TYPES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[280px]">
                      {t.type}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {t.result}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Callout type="note">
            Inventory Config decides when GRPO or Bill <B>adds</B> stock. A
            Goods Return debit note is the purchase-side return — it
            deducts. Credit Note Goods Return on sales <B>adds</B> stock
            back.
          </Callout>
          <Screenshot caption="Screenshot: Debit Note type Goods Return with warehouse quantity moving out." />

          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Viewing, searching, and the list
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Status tabs. No Excel export, no Duplicate. Detail: View
            Vendor, Download, Print. Stats: <B>Used Credit</B>,{" "}
            <B>Unused Credit</B> (available balance).
          </p>
          <Screenshot caption="Screenshot: Debit Notes list with status tabs Draft through Cancelled." />

          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Editing a debit note
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Draft only. Vendor and location stay locked.
          </p>

          <h3
            id="section-0-sub-4"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            5. Printing and downloading
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Detail: <B>Download</B> and <B>Print</B>.
          </p>

          <h3
            id="section-0-sub-5"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            6. Changing status
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Partially Used and Used are set by apply and refund, not Mark
            As.
          </p>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    From
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Mark As
                  </th>
                </tr>
              </thead>
              <tbody>
                {TRANSITIONS.map((t, i) => (
                  <tr
                    key={t.from}
                    className={
                      i < TRANSITIONS.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[180px]">
                      {t.from}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {t.to}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Screenshot caption="Screenshot: Mark As on an Open debit note — Closed, Draft, Cancelled." />

          <h3
            id="section-0-sub-6"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            7. Apply to Bill
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Open or Partially Used. Modal applies this note&apos;s available
            balance to Open / Partially Paid / Overdue bills. Cross-currency
            apply is blocked.
          </p>
          <Callout type="warning">
            Apply Credit on a bill header is vendor opening-balance credit.
            Apply to Bill here is this debit note.
          </Callout>
          <Screenshot caption="Screenshot: Apply to Bill using this debit note's unused credit." />

          <h3
            id="section-0-sub-7"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            8. Refund
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Open or Partially Used, available balance greater than zero.{" "}
            <B>To account</B> is a payment account (Cash / Bank / Payment
            Clearing / Credit Card). Can move status to Partially Used or
            Used. Delete refund reverses the journal and restores balance.
            Cancel the debit note only after refunds are deleted.
          </p>
          <Screenshot caption="Screenshot: Refund on an Open debit note with unused credit." />

          <h3
            id="section-0-sub-8"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            9. Deleting a debit note
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Hidden once anything is applied or refunded. Non-cancelled
            delete reverses bill applications, restores Current Balance,
            puts Goods Return stock back, and deletes the journal.
          </p>
          <Screenshot caption="Screenshot: Delete hidden because this debit note was applied to a bill." />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr
                    key={r.topic}
                    className={
                      i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/purchase/bills"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Bills <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              A bill is required, and Apply to Bill settles it
            </p>
          </Link>
          <Link
            href="/documentation/books/purchase/vendors"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Vendors <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Unused Credit on the vendor drawer is this module&apos;s
              available balance
            </p>
          </Link>
          <Link
            href="/documentation/books/sales/credit-notes"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Credit Notes <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Sales-side sibling — Goods Return adds stock there
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
